import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import { decrypt, encrypt } from "../helpers/encryptDecrypt";

// Create Context
const AuthContext = createContext({});

// Inactivity timeout - 30 minutes
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userRole, setUserRole] = useState(null);
    const [userStateId, setUserStateId] = useState(null);
    const [userStateName, setUserStateName] = useState(null);
    const [userName, setUserName] = useState(null);
    const [facilityId, setFacilityId] = useState(null);
    const [userId, setUserId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [abhaProfile, setAbhaProfile] = useState(null);

    // Refs for activity tracking
    const inactivityTimerRef = useRef(null);
    const lastActivityRef = useRef(Date.now());

    // Retrieve encrypted token from localStorage
    const getAuthToken = async () => {
        try {
            const token = await localStorage.getItem("app-token");
            return token;
        } catch (error) {
            console.error("Error retrieving token:", error);
            return null;
        }
    };

    // Retrieve encrypted user role from localStorage
    const getUserRole = async () => {
        try {
            const role = await localStorage.getItem("user-role");
            return role;
        } catch (error) {
            console.error("Error retrieving user role:", error);
            return null;
        }
    };

    // Retrieve encrypted user ID from localStorage
    const getUserId = async () => {
        try {
            const id = await localStorage.getItem("user-id");
            return id;
        } catch (error) {
            console.error("Error retrieving user ID:", error);
            return null;
        }
    };

    // Retrieve encrypted user name from localStorage
    const getUserName = async () => {
        try {
            const id = await localStorage.getItem("user-name");
            return id;
        } catch (error) {
            console.error("Error retrieving user name:", error);
            return null;
        }
    };

    // Retrieve encrypted facility ID from localStorage
    const getFacilityId = async () => {
        try {
            const id = await localStorage.getItem("facility-id");
            return id;
        } catch (error) {
            console.error("Error retrieving facility ID:", error);
            return null;
        }
    };

    // Retrieve encrypted user state ID from localStorage
    const getUserStateId = async () => {
        try {
            const id = await localStorage.getItem("user-state-id");
            return id;
        } catch (error) {
            console.error("Error retrieving user state ID:", error);
            return null;
        }
    };

    // Retrieve encrypted user state name from localStorage
    const getUserStateName = async () => {
        try {
            const name = await localStorage.getItem("user-state-name");
            return name;
        } catch (error) {
            console.error("Error retrieving user state name:", error);
            return null;
        }
    };

    // Retrieve encrypted token expiry from localStorage
    const getTokenExpiry = async () => {
        try {
            const expiry = await localStorage.getItem("token-expiry");
            return expiry;
        } catch (error) {
            console.error("Error retrieving token expiry:", error);
            return null;
        }
    };

    // Logout function - removes all auth data
    const logout = useCallback(async () => {
        try {
            // Clear inactivity timer
            if (inactivityTimerRef.current) {
                clearTimeout(inactivityTimerRef.current);
                inactivityTimerRef.current = null;
            }

            await localStorage.removeItem("app-token");
            await localStorage.removeItem("user-role");
            await localStorage.removeItem("user-name");
            await localStorage.removeItem("facility-id");
            await localStorage.removeItem("user-state-id");
            await localStorage.removeItem("user-state-name");
            await localStorage.removeItem("token-expiry");
            setIsAuthenticated(false);
            setUserRole(null);
            setUserId(null);
            setUserName(null);
            setFacilityId(null);
            setUserStateId(null);
            setAbhaProfile(null);
        } catch (error) {
            console.error("Logout error:", error);
        }
    }, []);

    // Update token expiry time based on current activity
    const updateTokenExpiry = useCallback(async () => {
        try {
            const newExpiryTime = Date.now() + INACTIVITY_TIMEOUT;
            const encryptedExpiry = encrypt(newExpiryTime.toString());
            await localStorage.setItem("token-expiry", JSON.stringify(encryptedExpiry));
            lastActivityRef.current = Date.now();
            // console.log("Token expiry updated to:", new Date(newExpiryTime).toLocaleString());
        } catch (error) {
            console.error("Error updating token expiry:", error);
        }
    }, []);

    // Handle user activity
    const handleUserActivity = useCallback(() => {
        // Only update if user is authenticated
        if (!isAuthenticated) return;

        // Reset the inactivity timer
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
        }

        // Update last activity time and token expiry
        updateTokenExpiry();

        // Set new inactivity timer
        inactivityTimerRef.current = setTimeout(() => {
            // console.log("User inactive for 30 minutes, logging out...");
            logout();
            // Dispatch event for token expiry
            window.dispatchEvent(new Event('tokenExpired'));
        }, INACTIVITY_TIMEOUT);
    }, [isAuthenticated, logout, updateTokenExpiry]);

    // Check authentication status
    const checkAuth = async () => {
        setLoading(true);
        try {
            const encryptedToken = await getAuthToken();
            const encryptedRole = await getUserRole();
            const encryptedUserId = await getUserId();
            const encryptedUserName = await getUserName();
            const encryptedFacilityId = await getFacilityId();
            const encryptedUserStateId = await getUserStateId();
            const encryptedUserStateName = await getUserStateName();
            const encryptedExpiry = await getTokenExpiry();

            if (encryptedToken) {
                // Decrypt token
                const decryptedToken = decrypt(JSON.parse(encryptedToken));

                // Check token expiry if available
                if (encryptedExpiry) {
                    const decryptedExpiry = decrypt(JSON.parse(encryptedExpiry));
                    const expiryTime = parseInt(decryptedExpiry);

                    // If token has expired, logout
                    if (Date.now() > expiryTime) {
                        // console.log("Token expired during auth check");
                        await logout();
                        return;
                    }
                }

                if (decryptedToken) {
                    setIsAuthenticated(true);

                    // Decrypt and set user role if available
                    if (encryptedRole) {
                        const decryptedRole = decrypt(JSON.parse(encryptedRole));
                        setUserRole(parseInt(decryptedRole, 10));
                    }

                    // Decrypt and set user ID if available
                    if (encryptedUserId) {
                        const decryptedUserId = decrypt(JSON.parse(encryptedUserId));
                        setUserId(decryptedUserId);
                    }

                    // Decrypt and set user name if available
                    if (encryptedUserName) {
                        const decryptedUserName = decrypt(JSON.parse(encryptedUserName));
                        setUserName(decryptedUserName);
                    }

                    // Decrypt and set facility ID if available
                    if (encryptedFacilityId) {
                        const decryptedFacilityId = decrypt(JSON.parse(encryptedFacilityId));
                        setFacilityId(decryptedFacilityId);
                    }

                    // Decrypt and set user state ID if available
                    if (encryptedUserStateId) {
                        const decryptedUserStateId = decrypt(JSON.parse(encryptedUserStateId));
                        setUserStateId(decryptedUserStateId);
                    }

                    // Decrypt and set user state name if available
                    if (encryptedUserStateName) {
                        const decryptedUserStateName = decrypt(JSON.parse(encryptedUserStateName));
                        setUserStateName(decryptedUserStateName);
                    }

                    // Note: handleUserActivity will be triggered by the useEffect
                    // after isAuthenticated is set to true
                } else {
                    setIsAuthenticated(false);
                    setUserRole(null);
                    setUserId(null);
                    setUserName(null);
                    setFacilityId(null);
                }
            } else {
                setIsAuthenticated(false);
                setUserRole(null);
                setUserId(null);
                setUserName(null);
                setFacilityId(null);
            }
        } catch (error) {
            console.error("Auth check error:", error);
            setIsAuthenticated(false);
            setUserRole(null);
            setUserId(null);
            setUserName(null);
            setFacilityId(null);
        } finally {
            setLoading(false);
        }
    };

    // Login function - stores encrypted token and role
    const login = async (encryptedToken, encryptedRole, encryptedUserId, encryptedFacilityId, encryptedUserName) => {
        try {
            // Token and role are already encrypted from Login.jsx
            await localStorage.setItem("app-token", JSON.stringify(encryptedToken));
            await localStorage.setItem("user-role", JSON.stringify(encryptedRole));
            await localStorage.setItem("user-id", JSON.stringify(encryptedUserId));
            await localStorage.setItem("user-name", JSON.stringify(encryptedUserName));
            await localStorage.setItem("facility-id", JSON.stringify(encryptedFacilityId));

            // Decrypt for state management
            const decryptedRole = parseInt(decrypt(encryptedRole), 10);
            const decryptedUserId = decrypt(encryptedUserId);
            const decryptedUserName = decrypt(encryptedUserName);
            const decryptedFacilityId = decrypt(encryptedFacilityId);

            setIsAuthenticated(true);
            setUserRole(decryptedRole);
            setUserId(decryptedUserId);
            setUserName(decryptedUserName);
            setFacilityId(decryptedFacilityId);

            // Start tracking user activity after login
            // Note: The activity tracking will be initiated by the useEffect
            // when isAuthenticated becomes true
        } catch (error) {
            console.error("Login error:", error);
            throw error;
        }
    };

    // Wrapper for setUserStateId to sync with localStorage
    const handleSetUserStateId = (id) => {
        setUserStateId(id);
        if (id) {
            localStorage.setItem("user-state-id", JSON.stringify(encrypt(id.toString())));
        } else {
            localStorage.removeItem("user-state-id");
        }
    };

    // Wrapper for setUserStateName to sync with localStorage
    const handleSetUserStateName = (name) => {
        setUserStateName(name);
        if (name) {
            localStorage.setItem("user-state-name", JSON.stringify(encrypt(name.toString())));
        } else {
            localStorage.removeItem("user-state-name");
        }
    };

    // Listen for token expiry event
    useEffect(() => {
        const handleTokenExpiry = () => {
            // console.log("Token expired event received, logging out...");
            logout();
        };

        window.addEventListener('tokenExpired', handleTokenExpiry);

        return () => {
            window.removeEventListener('tokenExpired', handleTokenExpiry);
        };
    }, [logout]);

    // Set up activity listeners when user is authenticated
    useEffect(() => {
        if (!isAuthenticated) {
            // Clear timer if user is not authenticated
            if (inactivityTimerRef.current) {
                clearTimeout(inactivityTimerRef.current);
                inactivityTimerRef.current = null;
            }
            return;
        }

        // Initial activity setup when user becomes authenticated
        handleUserActivity();

        // Activity events to track
        const activityEvents = [
            'mousedown',
            'mousemove',
            'keypress',
            'scroll',
            'touchstart',
            'click',
        ];

        // Throttle function to prevent too many updates
        let throttleTimer = null;
        const throttledActivityHandler = () => {
            if (!throttleTimer) {
                throttleTimer = setTimeout(() => {
                    handleUserActivity();
                    throttleTimer = null;
                }, 5000); // Update every 5 seconds max
            }
        };

        // Add event listeners
        activityEvents.forEach(event => {
            window.addEventListener(event, throttledActivityHandler);
        });

        // Cleanup
        return () => {
            activityEvents.forEach(event => {
                window.removeEventListener(event, throttledActivityHandler);
            });
            if (throttleTimer) {
                clearTimeout(throttleTimer);
            }
        };
    }, [isAuthenticated, handleUserActivity]);

    // Check auth on mount
    useEffect(() => {
        checkAuth();
    }, []);

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                userRole,
                userId,
                userName,
                facilityId,
                userStateId,
                userStateName,
                loading,
                login,
                logout,
                checkAuth,
                getAuthToken,
                getUserId,
                getUserName,
                abhaProfile,
                setAbhaProfile,
                setUserName,
                setFacilityId,
                setUserStateId: handleSetUserStateId,
                setUserStateName: handleSetUserStateName,
                updateTokenExpiry
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export { AuthContext, AuthProvider };